import { ChevronLeft, CheckCircle, MessageCircle } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function SMSConsent() {
  return (
    <div className="min-h-screen bg-rdc-cream">
      {/* Header */}
      <div className="bg-white border-b border-rdc-stone/30">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-charcoal transition-colors mb-6"
          >
            <ChevronLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <h1 className="page-title">SMS Communication Consent</h1>
          <p className="text-rdc-taupe mt-2">
            Twilio Toll-Free SMS Registration Documentation
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-sm max-w-none text-rdc-charcoal">
          {/* Introduction */}
          <section className="mb-8">
            <p className="text-base leading-relaxed">
              This page documents how Riding & Driving Club ("RDC") collects explicit consent from users before sending SMS (text) messages. This documentation is provided to meet Twilio's requirements for toll-free SMS registration and demonstrates RDC's commitment to compliant, ethical SMS messaging practices.
            </p>
          </section>

          {/* Overview */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">Consent Collection Overview</h2>
            <p className="text-base leading-relaxed mb-6">
              Riding & Driving Club collects explicit, documented consent from users before any SMS messages are sent. Consent is collected through multiple touchpoints in the user journey, ensuring that users are fully informed and actively opt-in to receive messages.
            </p>

            <div className="bg-rdc-cream/70 rounded-lg p-6 border border-rdc-stone/30">
              <div className="flex items-start gap-3">
                <MessageCircle size={24} className="text-rdc-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-rdc-black mb-2">Consent Principle</p>
                  <p className="text-sm text-rdc-charcoal">
                    Users must explicitly opt-in to receive SMS notifications. We do not use pre-checked boxes or assumed consent. Every user action is intentional and documented.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Consent During Signup */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">1. Consent Collection During Sign-Up</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-4">Sign-Up Form</h3>
              <p className="text-base leading-relaxed mb-4">
                When users create a new RDC Digital Garage account, they encounter a phone number field with explicit SMS consent language:
              </p>

              {/* Signup Mockup */}
              <div className="bg-white rounded-lg border border-rdc-stone/30 overflow-hidden shadow-sm mb-6">
                <div className="p-6">
                  <h4 className="font-semibold text-rdc-black mb-4">Account Setup - Phone Number</h4>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        disabled
                        className="w-full px-3 py-2 border border-rdc-stone rounded-lg bg-rdc-cream/50 text-rdc-charcoal"
                      />
                      <p className="text-xs text-rdc-taupe mt-2">
                        Used for SMS appointment reminders and service notifications
                      </p>
                    </div>

                    {/* Consent Checkbox */}
                    <div className="mt-6 p-4 bg-rdc-cream/50 rounded-lg border border-rdc-primary/20">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="sms-consent"
                          disabled
                          className="mt-1 w-4 h-4 text-rdc-primary rounded"
                        />
                        <label htmlFor="sms-consent" className="text-sm text-rdc-charcoal leading-relaxed cursor-pointer">
                          <span className="font-semibold">I agree to receive SMS notifications</span> from Riding & Driving Club regarding work orders, appointment reminders, service alerts, and concierge updates. Message frequency varies. <span className="font-semibold">Message and data rates may apply.</span> Reply STOP to unsubscribe. <span className="font-semibold">Reply HELP for help.</span>
                        </label>
                      </div>
                    </div>

                    <div className="p-3 bg-rdc-gold/10 rounded-lg border border-rdc-gold/20">
                      <p className="text-xs text-rdc-charcoal">
                        <span className="font-semibold">Note:</span> This checkbox is unchecked by default. Users must actively check this box to enable SMS communications. We do not pre-check this box.
                      </p>
                    </div>
                  </div>

                  <div className="text-xs text-rdc-taupe">
                    <p className="mb-2">By submitting this form, you agree to our <Link to="/terms" className="text-rdc-primary hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-rdc-primary hover:underline">Privacy Policy</Link>.</p>
                  </div>
                </div>
              </div>

              <p className="text-base leading-relaxed text-sm">
                <strong>Key Elements:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-rdc-charcoal mb-4">
                <li>Phone number field is optional, reducing friction</li>
                <li>Consent checkbox is <strong>unchecked by default</strong></li>
                <li>Clear, plain-language description of message types</li>
                <li>Explicit mention of message rates and opt-out instructions (STOP)</li>
                <li>Clear help instructions (HELP)</li>
                <li>Links to full legal documents (Terms & Privacy)</li>
              </ul>
            </div>
          </section>

          {/* Consent in Settings */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">2. Consent Collection in Settings</h2>

            <p className="text-base leading-relaxed mb-4">
              Users can also enable or modify SMS consent at any time through their account settings:
            </p>

            {/* Settings Mockup */}
            <div className="bg-white rounded-lg border border-rdc-stone/30 overflow-hidden shadow-sm mb-6">
              <div className="p-6">
                <h4 className="font-semibold text-rdc-black mb-4">Settings - Notification Preferences</h4>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-rdc-cream rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-black">SMS Notifications</p>
                      <p className="text-xs text-rdc-taupe mt-1">Receive text message notifications</p>
                    </div>
                    <button
                      disabled
                      className="relative inline-flex h-8 w-14 items-center rounded-full bg-rdc-warm-gray"
                    >
                      <span className="inline-block h-6 w-6 transform rounded-full bg-white transition-transform translate-x-1" />
                    </button>
                  </div>

                  <div className="p-4 bg-rdc-cream/50 rounded-lg border border-rdc-stone/30">
                    <p className="text-xs text-rdc-taupe leading-relaxed">
                      <span className="font-semibold">What messages you'll receive:</span> Work order updates, appointment reminders, service alerts, and concierge notifications. Standard SMS rates apply. You can change this setting at any time or reply STOP to any message to opt out.
                    </p>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-rdc-forest/5 rounded-lg border border-rdc-forest/10">
                    <CheckCircle size={18} className="text-rdc-forest flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-rdc-charcoal">
                      When enabled, you are consenting to receive SMS messages as described above. Your consent is recorded and dated.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Consent Documentation */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">3. Consent Documentation & Storage</h2>

            <div className="space-y-4 mb-6">
              <div className="bg-rdc-cream/70 rounded-lg p-6 border border-rdc-stone/30">
                <h3 className="font-semibold text-rdc-black mb-3">What We Document</h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-rdc-charcoal">
                  <li><strong>Timestamp:</strong> Exact date and time consent was given</li>
                  <li><strong>Phone Number:</strong> The specific phone number consented to</li>
                  <li><strong>Consent Type:</strong> Explicit opt-in via checkbox or settings</li>
                  <li><strong>User IP Address:</strong> Location of consent action</li>
                  <li><strong>Method:</strong> Whether consent was given during signup or settings update</li>
                  <li><strong>Consent Text:</strong> The exact language shown to the user</li>
                </ul>
              </div>
            </div>

            <p className="text-base leading-relaxed mb-4">
              <strong>Retention:</strong> Consent records are retained indefinitely to demonstrate compliance with regulatory requirements and to honor user preferences.
            </p>
          </section>

          {/* Message Types */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">4. Types of SMS Messages Sent</h2>

            <div className="space-y-4">
              <div className="border-l-4 border-rdc-primary pl-4 py-2">
                <h3 className="font-semibold text-rdc-black text-sm">Transactional Messages</h3>
                <p className="text-sm text-rdc-charcoal mt-1">
                  Work order status updates, appointment confirmations, and critical service notifications that are essential to the user experience.
                </p>
                <p className="text-xs text-rdc-taupe mt-2">Example: "Your vehicle service is scheduled for Thursday at 2:00 PM at Smith Auto Service."</p>
              </div>

              <div className="border-l-4 border-rdc-forest pl-4 py-2">
                <h3 className="font-semibold text-rdc-black text-sm">Informational Messages</h3>
                <p className="text-sm text-rdc-charcoal mt-1">
                  Service reminders, maintenance alerts, vehicle status notifications, and appointment reminders based on user preferences.
                </p>
                <p className="text-xs text-rdc-taupe mt-2">Example: "Reminder: Your vehicle registration expires in 30 days."</p>
              </div>

              <div className="border-l-4 border-rdc-saddle pl-4 py-2">
                <h3 className="font-semibold text-rdc-black text-sm">Concierge Messages</h3>
                <p className="text-sm text-rdc-charcoal mt-1">
                  Updates and responses from our AI concierge service regarding user requests and vehicle inquiries.
                </p>
                <p className="text-xs text-rdc-taupe mt-2">Example: "Your concierge has scheduled a service appointment. Details sent to your email."</p>
              </div>

              <div className="border-l-4 border-rdc-burgundy pl-4 py-2">
                <h3 className="font-semibold text-rdc-black text-sm">Account & Security Messages</h3>
                <p className="text-sm text-rdc-charcoal mt-1">
                  Password resets, security alerts, and critical account notifications.
                </p>
                <p className="text-xs text-rdc-taupe mt-2">Example: "Your password was changed on Feb 16 at 2:30 PM. If this wasn't you, contact support."</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-rdc-cream/50 rounded-lg border border-rdc-stone/30">
              <p className="text-xs text-rdc-charcoal">
                <span className="font-semibold">Message Frequency:</span> Varies based on user activity and preferences. Typical range: 1-5 messages per month, but can be higher during periods of active service coordination.
              </p>
            </div>
          </section>

          {/* Opt-Out & Help */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">5. Opt-Out and Help Instructions</h2>

            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-rdc-stone/30 p-6">
                <h3 className="font-semibold text-rdc-black mb-3">Every RDC SMS Message Includes:</h3>

                <div className="space-y-4 text-sm text-rdc-charcoal">
                  <div className="p-3 bg-rdc-cream rounded-lg font-mono text-xs text-rdc-charcoal border border-rdc-stone/30">
                    <p className="mb-4">Your appointment is scheduled for Thu, Feb 20 at 2:00 PM at Smith Auto Service.</p>
                    <p className="text-rdc-taupe">---</p>
                    <p className="text-rdc-taupe mt-2">Reply STOP to opt out. Reply HELP for help. Msg & data rates may apply.</p>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-rdc-forest/5 rounded-lg border border-rdc-forest/10">
                    <CheckCircle size={16} className="text-rdc-forest flex-shrink-0 mt-0.5" />
                    <p className="text-xs">
                      Every message footer includes clear, compliant opt-out (STOP) and help (HELP) instructions
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-rdc-stone/30 p-6">
                <h3 className="font-semibold text-rdc-black mb-3">User Actions</h3>

                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-rdc-black">User replies "STOP"</p>
                    <p className="text-rdc-charcoal">Immediately unsubscribed from all SMS messages. Confirmation message sent: "You've been unsubscribed. You won't receive messages from RDC."</p>
                  </div>

                  <div className="border-t border-rdc-stone/30 pt-3">
                    <p className="font-medium text-rdc-black">User replies "HELP"</p>
                    <p className="text-rdc-charcoal">Receives help instructions: "Contact support at michael@ridinganddrivingclub.com for questions about your RDC account and messages."</p>
                  </div>

                  <div className="border-t border-rdc-stone/30 pt-3">
                    <p className="font-medium text-rdc-black">User changes preferences in settings</p>
                    <p className="text-rdc-charcoal">Can disable SMS notifications at any time through account settings</p>
                  </div>

                  <div className="border-t border-rdc-stone/30 pt-3">
                    <p className="font-medium text-rdc-black">User contacts support</p>
                    <p className="text-rdc-charcoal">michael@ridinganddrivingclub.com can assist with any SMS preference changes</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Compliance & Monitoring */}
          <section className="mb-10">
            <h2 className="section-title mb-4 text-rdc-black">6. Compliance & Monitoring</h2>

            <div className="space-y-4">
              <div className="bg-rdc-cream/70 rounded-lg p-6 border border-rdc-stone/30">
                <h3 className="font-semibold text-rdc-black mb-3">RDC's Commitment to Compliance</h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-rdc-charcoal">
                  <li>All SMS sent through Twilio-provided infrastructure</li>
                  <li>No messages sent without explicit documented consent</li>
                  <li>Immediate processing of STOP requests (within 4 hours per Twilio SLA)</li>
                  <li>Regular audit of consent records and message logs</li>
                  <li>Compliance with TCPA, GDPR, and other regulatory requirements</li>
                  <li>Honoring all user preference changes and opt-out requests</li>
                  <li>Clear, compliant messaging footer on every SMS</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Consent Summary Table */}
          <section className="mb-12">
            <h2 className="section-title mb-4 text-rdc-black">7. Consent Collection Summary</h2>

            <div className="overflow-x-auto rounded-lg border border-rdc-stone/30">
              <table className="w-full text-sm">
                <thead className="bg-rdc-cream border-b border-rdc-stone/30">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-rdc-black">Touchpoint</th>
                    <th className="px-4 py-3 text-left font-semibold text-rdc-black">Consent Method</th>
                    <th className="px-4 py-3 text-left font-semibold text-rdc-black">Default State</th>
                    <th className="px-4 py-3 text-left font-semibold text-rdc-black">Explicit Opt-In</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rdc-stone/30">
                    <td className="px-4 py-3 text-rdc-charcoal">Account Sign-Up</td>
                    <td className="px-4 py-3 text-rdc-charcoal">Checkbox with consent text</td>
                    <td className="px-4 py-3 text-rdc-charcoal">Unchecked</td>
                    <td className="px-4 py-3 text-rdc-charcoal">
                      <CheckCircle size={16} className="text-rdc-forest" />
                    </td>
                  </tr>
                  <tr className="border-b border-rdc-stone/30">
                    <td className="px-4 py-3 text-rdc-charcoal">Settings Update</td>
                    <td className="px-4 py-3 text-rdc-charcoal">Toggle with informational text</td>
                    <td className="px-4 py-3 text-rdc-charcoal">User-dependent</td>
                    <td className="px-4 py-3 text-rdc-charcoal">
                      <CheckCircle size={16} className="text-rdc-forest" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-rdc-charcoal">Documented & Logged</td>
                    <td className="px-4 py-3 text-rdc-charcoal">Timestamp, IP, method, text</td>
                    <td className="px-4 py-3 text-rdc-charcoal">Full audit trail</td>
                    <td className="px-4 py-3 text-rdc-charcoal">
                      <CheckCircle size={16} className="text-rdc-forest" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* For Twilio Review */}
          <section className="mb-12 p-6 bg-rdc-gold/10 rounded-lg border border-rdc-gold/30">
            <h2 className="section-title mb-4 text-rdc-black">For Twilio Compliance Review</h2>
            <p className="text-base leading-relaxed">
              This page serves as documentation of Riding & Driving Club's SMS consent collection practices for Twilio's toll-free SMS registration requirements. It demonstrates:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-rdc-charcoal mt-4">
              <li>Explicit, documented consent collection before any SMS is sent</li>
              <li>Clear disclosure of message types and frequency</li>
              <li>Plain-language consent language understandable to users</li>
              <li>Compliant opt-out mechanisms (STOP) on every message</li>
              <li>Help instructions (HELP) on every message</li>
              <li>Unchecked-by-default consent to prevent implied consent</li>
              <li>Full audit trail and documentation of all consent</li>
              <li>Immediate processing of opt-out requests</li>
              <li>Compliance with TCPA and related regulations</li>
            </ul>
            <p className="text-sm text-rdc-taupe mt-4">
              For questions about SMS messaging practices, contact michael@ridinganddrivingclub.com
            </p>
          </section>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-rdc-stone/30 flex gap-6 justify-center text-sm">
            <Link to="/privacy" className="text-rdc-primary hover:underline">
              Privacy Policy
            </Link>
            <span className="text-rdc-stone">/</span>
            <Link to="/terms" className="text-rdc-primary hover:underline">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
